import { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ActivateIcon from '@mui/icons-material/CheckCircle';
import {
    Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, TablePagination
} from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { fetchData, deleteData, activateData, postData, updateData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import Button from '@mui/material/Button';
import Info from '@mui/icons-material/Info';
import AddIcon from '@mui/icons-material/Add';
import CopyrightIcon from '@mui/icons-material/Copyright';
import RefreshIcon from '@mui/icons-material/Refresh';
import { Tooltip, Fade, CircularProgress, Backdrop } from '@mui/material';
import { useLocation, Link } from 'react-router-dom';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';

function IncomeExpenseIndex() {
    const [data, setData] = useState([]);
    const [deleteItemId, setDeleteItemId] = useState(null);
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const history = useHistory();
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const [isFocused, setIsFocused] = useState(false);
    const [mounted, setMounted] = useState(false);

    const { isAuthenticated, companyId, permissions, token, logout, userId } = useAuth();
    const hasCreateMasterPermission = permissions.includes('Create Accounts');
    const hasEditMasterPermission = permissions.includes('Edit Accounts');
    const hasDeleteMasterPermission = permissions.includes('Delete Accounts');
    const location = useLocation();
    const [loading, setLoading] = useState(false);

    // Account Head Modal States
    const [accountHeadModalOpen, setAccountHeadModalOpen] = useState(false);
    const [accountHeadData, setAccountHeadData] = useState([]);
    const [accountHeadName, setAccountHeadName] = useState('');
    const [isExpenseOrIncome, setIsExpenseOrIncome] = useState(false);
    const [accountHeadLoading, setAccountHeadLoading] = useState(false);
    const [accountHeadError, setAccountHeadError] = useState('');
    const [accountHeadSuccess, setAccountHeadSuccess] = useState('');
    const [editAccountHeadId, setEditAccountHeadId] = useState(null);
    const [accountHeadSearchQuery, setAccountHeadSearchQuery] = useState('');

    useEffect(() => {
        setMounted(true);
        const shouldReload = localStorage.getItem('shouldReload') === 'true';

        if (location.state?.from === '/IncomeExpenseCreate' && shouldReload) {
            localStorage.removeItem('shouldReload');
            window.location.reload();
        }
        else if (location.state?.from === '/IncomeExpenseEdit' && shouldReload) {
            localStorage.removeItem('shouldReload');
            window.location.reload();
        }
        else if (location.state?.from === '/IncomeExpenseDetails' && shouldReload) {
            localStorage.removeItem('shouldReload');
            window.location.reload();
        }
        else {
            setFromDate(getFormattedStartDate());
            setToDate(getFormattedCurrentDate());
        }
    }, [location]);
  

    const [searchQuery, setSearchQuery] = useState('');

    const handleSearch = (event) => {
        setSearchQuery(event.target.value);
    };

    const filteredData = data.filter((item) =>
        Object.values(item).some((value) =>
            String(value).toLowerCase().includes(searchQuery.toLowerCase())
        )
    );

    const getFormattedCurrentDate = () => {
        const currentDate = new Date();
        return currentDate.toISOString().split('T')[0];
    };

    const getFormattedStartDate = () => {
        const monthStartDate = new Date();
        monthStartDate.setDate(1);
        return monthStartDate.toISOString().split('T')[0];
    };

    const handleFromDateChange = (e) => {
        const selectedDate = e.target.value;
        setFromDate(selectedDate);
    };

    const handleToDateChange = (e) => {
        const selectedDate = e.target.value;
        setToDate(selectedDate);
    };

    const fetchTableData = async () => {
        setLoading(true);
        setData([]);
        try {
            const response = await fetchData('IncomeExpenseApi', `/${companyId}/${fromDate}/${toDate}`, token);
            if (response.responseCode === '1111') {
                setData(response.response_data);
            }
        } catch (error) {
            console.error('Error fetching SaleOrder Details:', error);
            if (error.response && error.response.status === 401) {
                logout();
                history.replace(`/login?redirect=${encodeURIComponent(window.location.pathname)}`);
            }
        } finally {
            setLoading(false);
        }

    };

    const handleDelete = async () => {
        deleteData('IncomeExpenseApi', `${deleteItemId}`, token)
            .then(() => {
                fetchTableData();
                setDeleteItemId(null);
                setConfirmDialogOpen(false);
            })
            .catch((error) => {
                console.error('Error deleting record:', error);
            });
    };

    const handleActivate = (itemId) => {
        activateData('IncomeExpenseApi', `${itemId}`, token)
            .then(() => {
                fetchTableData();
            })
            .catch((error) => {
                console.error('Error activating record:', error);
            });
    };

    const openConfirmDialog = (itemId) => {
        setDeleteItemId(itemId);
        setConfirmDialogOpen(true);
    };

    const closeConfirmDialog = () => {
        setDeleteItemId(null);
        setConfirmDialogOpen(false);
    };

    function formatDate(dateString) {
        const options = { year: 'numeric', month: '2-digit', day: '2-digit' };
        const formattedDate = new Date(dateString).toLocaleDateString('en-GB', options);
        return formattedDate;
    }

    const [order, setOrder] = useState('desc');
    const [orderBy, setOrderBy] = useState('PurchaseOrderDate');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const sortedData = stableSort(filteredData, getComparator(order, orderBy));

    // Account Head Functions
    const openAccountHeadModal = () => {
        setAccountHeadModalOpen(true);
        fetchAccountHeadData();
    };

    const closeAccountHeadModal = () => {
        setAccountHeadModalOpen(false);
        setAccountHeadName('');
        setIsExpenseOrIncome(false);
        setAccountHeadError('');
        setAccountHeadSuccess('');
        setEditAccountHeadId(null);
    };

    const fetchAccountHeadData = async () => {
        setAccountHeadLoading(true);
        try {
            const response = await fetchData('AccountHeadApi', `/${companyId}`, token);
            if (response.responseCode === '1111') {
                setAccountHeadData(response.response_data);
            }
        } catch (error) {
            console.error('Error fetching Account Head data:', error);
        } finally {
            setAccountHeadLoading(false);
        }
    };

    const handleAccountHeadSubmit = async (e) => {
        e.preventDefault();
        setAccountHeadLoading(true);
        setAccountHeadError('');

        if (!accountHeadName.trim()) {
            setAccountHeadError('Account Head Name is required!');
            setAccountHeadLoading(false);
            return;
        }

        // Check for duplicates
        const isDuplicate = accountHeadData.some(
            (item) => item.Name.trim().toLowerCase() === accountHeadName.trim().toLowerCase() && item.Id !== editAccountHeadId
        );

        if (isDuplicate) {
            setAccountHeadError('This Account Head Name already exists!');
            setAccountHeadLoading(false);
            return;
        }

        try {
            if (editAccountHeadId) {
                // Update existing
                const updatedData = {
                    Id: editAccountHeadId,
                    CompanyId: companyId,
                    Name: accountHeadName,
                    IsExpenseOrIncome: isExpenseOrIncome,
                };
                await updateData('AccountHeadApi', `${editAccountHeadId}`, updatedData, token);
                setAccountHeadSuccess('Account Head updated successfully!');
            } else {
                // Create new
                const newAccountHead = {
                    CompanyId: companyId,
                    Name: accountHeadName,
                    IsExpenseOrIncome: isExpenseOrIncome,
                    IsActive: true,
                    CreatedBy: userId,
                    CreatedDate: new Date(),
                };
                await postData('AccountHeadApi', newAccountHead, token);
                setAccountHeadSuccess('Account Head created successfully!');
            }

            setAccountHeadName('');
            setIsExpenseOrIncome(false);
            setEditAccountHeadId(null);
            fetchAccountHeadData();

            setTimeout(() => {
                setAccountHeadSuccess('');
            }, 3000);
        } catch (error) {
            console.error('Error saving Account Head:', error);
            setAccountHeadError('Failed to save Account Head');
        } finally {
            setAccountHeadLoading(false);
        }
    };

    const handleEditAccountHead = (item) => {
        setEditAccountHeadId(item.Id);
        setAccountHeadName(item.Name);
        setIsExpenseOrIncome(item.IsExpenseOrIncome);
        setAccountHeadError('');
        setAccountHeadSuccess('');
    };

    const handleDeleteAccountHead = async (id) => {
        try {
            await deleteData('AccountHeadApi', `${id}`, token);
            fetchAccountHeadData();
        } catch (error) {
            console.error('Error deleting Account Head:', error);
        }
    };

    const filteredAccountHeadData = accountHeadData.filter((item) =>
        item.Name.toLowerCase().includes(accountHeadSearchQuery.toLowerCase())
    );

    return (
        <Fade in={mounted} timeout={800}>
            <div style={{
                minWidth: '100%',
                background: '#f5f7fa',
                padding: '10px',
                position: 'relative'
            }}>
                <style>{`
                    @keyframes slideIn {
                        from { opacity: 0; transform: translateY(20px); }
                        to { opacity: 1; transform: translateY(0); }
                    }
                    .action-button {
                        transition: all 0.3s ease;
                        text-transform: none;
                        font-weight: 600;
                        letter-spacing: 0.5px;
                    }
                    .action-button:hover {
                        transform: translateY(-2px);
                        box-shadow: 0 8px 20px rgba(0, 0, 0, 0.2);
                    }
                    .table-row {
                        transition: all 0.2s ease;
                    }
                    .table-row:hover {
                        background: #f8f9fa !important;
                        transform: scale(1.01);
                    }
                `}</style>

                {loading && (
                    <Backdrop
                        open={loading}
                        sx={{
                            color: '#fff',
                            zIndex: 9999,
                            backdropFilter: 'blur(4px)',
                            backgroundColor: 'rgba(0, 0, 0, 0.7)'
                        }}
                    >
                        <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: '20px'
                        }}>
                            <CircularProgress
                                size={60}
                                thickness={4}
                                sx={{
                                    color: '#667eea',
                                    '& .MuiCircularProgress-circle': {
                                        strokeLinecap: 'round',
                                    }
                                }}
                            />
                            <div style={{
                                fontSize: '16px',
                                fontWeight: '600',
                                color: 'white',
                                letterSpacing: '0.5px',
                                textAlign: 'center'
                            }}>
                                Loading Income & Expense Data...
                            </div>
                        </div>
                    </Backdrop>
                )}

                <div style={{
                    maxWidth: '1400px',
                    margin: '0 auto',
                    background: 'white',
                    borderRadius: '12px',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                    padding: '12px',
                    position: 'relative'
                }}>
                    {/* Header Section */}
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '8px',
                        paddingBottom: '6px',
                        borderBottom: '1px solid #f0f0f0',
                        flexWrap: 'wrap',
                        gap: '8px'
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <div style={{
                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                padding: '6px',
                                borderRadius: '8px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                boxShadow: '0 2px 6px rgba(102, 126, 234, 0.3)'
                            }}>
                                <AccountBalanceWalletIcon style={{ fontSize: '18px', color: 'white' }} />
                            </div>
                            <div>
                                <h1 style={{
                                    fontSize: '16px',
                                    fontWeight: '700',
                                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                    WebkitBackgroundClip: 'text',
                                    WebkitTextFillColor: 'transparent',
                                    margin: 0,
                                    letterSpacing: '-0.3px'
                                }}>
                                    Income & Expense
                                </h1>
                                <p style={{ margin: 0, color: '#666', fontSize: '10px' }}>
                                    Manage income and expense records
                                </p>
                            </div>
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                            <input
                                type="text"
                                placeholder="Search..."
                                value={searchQuery}
                                onChange={handleSearch}
                                onFocus={() => setIsFocused(true)}
                                onBlur={() => setIsFocused(false)}
                                style={{
                                    width: isFocused ? '180px' : '130px',
                                    height: '28px',
                                    border: '1px solid #e0e0e0',
                                    borderRadius: '6px',
                                    padding: '0 10px',
                                    transition: 'all 0.3s ease',
                                    outline: 'none',
                                    borderColor: isFocused ? '#667eea' : '#e0e0e0',
                                    boxShadow: isFocused ? '0 2px 6px rgba(102, 126, 234, 0.2)' : 'none',
                                    fontSize: '12px'
                                }}
                            />

                            <input
                                type="date"
                                value={fromDate}
                                onChange={handleFromDateChange}
                                style={{
                                    height: '28px',
                                    border: '1px solid #e0e0e0',
                                    borderRadius: '6px',
                                    padding: '0 8px',
                                    fontSize: '11px',
                                    outline: 'none'
                                }}
                            />
                            <input
                                type="date"
                                value={toDate}
                                onChange={handleToDateChange}
                                style={{
                                    height: '28px',
                                    border: '1px solid #e0e0e0',
                                    borderRadius: '6px',
                                    padding: '0 8px',
                                    fontSize: '11px',
                                    outline: 'none'
                                }}
                            />

                            <Tooltip title="Refresh data" arrow>
                                <Button
                                    variant="contained"
                                    onClick={fetchTableData}
                                    startIcon={<RefreshIcon style={{ fontSize: '16px' }} />}
                                    className="action-button"
                                    style={{
                                        background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                                        color: 'white',
                                        padding: '4px 12px',
                                        borderRadius: '6px',
                                        fontSize: '12px',
                                        boxShadow: '0 2px 6px rgba(17, 153, 142, 0.3)',
                                        border: 'none',
                                        minHeight: '28px'
                                    }}
                                >
                                    Refresh
                                </Button>
                            </Tooltip>

                            <Tooltip title="Manage Account Heads" arrow>
                                <Button
                                    variant="contained"
                                    onClick={openAccountHeadModal}
                                    startIcon={<AccountBalanceIcon style={{ fontSize: '16px' }} />}
                                    className="action-button"
                                    style={{
                                        background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                                        color: 'white',
                                        padding: '4px 12px',
                                        borderRadius: '6px',
                                        fontSize: '12px',
                                        boxShadow: '0 2px 6px rgba(245, 87, 108, 0.3)',
                                        border: 'none',
                                        minHeight: '28px'
                                    }}
                                >
                                    Account Head
                                </Button>
                            </Tooltip>

                            {hasCreateMasterPermission && (
                                <Tooltip title="Create new income or expense" arrow>
                                    <Button
                                        variant="contained"
                                        component={Link}
                                        to="/IncomeExpenseCreate"
                                        startIcon={<AddIcon style={{ fontSize: '16px' }} />}
                                        className="action-button"
                                        style={{
                                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                            color: 'white',
                                            padding: '4px 12px',
                                            borderRadius: '6px',
                                            fontSize: '12px',
                                            boxShadow: '0 2px 6px rgba(102, 126, 234, 0.3)',
                                            border: 'none',
                                            minHeight: '28px'
                                        }}
                                    >
                                        Create New
                                    </Button>
                                </Tooltip>
                            )}
                        </div>
                    </div>

                    {/* Table */}
                    <div style={{ marginBottom: '10px' }}>
                        <TableContainer component={Paper} style={{
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
                            borderRadius: '10px',
                            overflow: 'hidden'
                        }}>
                            <Table sx={{ minWidth: 650 }} aria-label="income expense table">
                                <TableHead>
                                    <TableRow style={{
                                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                                    }}>
                                        <TableCell style={{
                                            color: 'white',
                                            fontWeight: '600',
                                            fontSize: '11px',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.5px',
                                            padding: '8px 12px'
                                        }}>
                                            Sl.No
                                        </TableCell>
                                        <TableCell style={{
                                            color: 'white',
                                            fontWeight: '600',
                                            fontSize: '11px',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.5px',
                                            padding: '8px 12px'
                                        }}>
                                            Bill Date
                                        </TableCell>
                                        <TableCell style={{
                                            color: 'white',
                                            fontWeight: '600',
                                            fontSize: '11px',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.5px',
                                            padding: '8px 12px'
                                        }}>
                                            Bill No
                                        </TableCell>
                                        <TableCell style={{
                                            color: 'white',
                                            fontWeight: '600',
                                            fontSize: '11px',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.5px',
                                            padding: '8px 12px'
                                        }}>
                                            Account Head
                                        </TableCell>
                                        <TableCell style={{
                                            color: 'white',
                                            fontWeight: '600',
                                            fontSize: '11px',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.5px',
                                            padding: '8px 12px'
                                        }}>
                                            Type
                                        </TableCell>
                                        <TableCell style={{
                                            color: 'white',
                                            fontWeight: '600',
                                            fontSize: '11px',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.5px',
                                            padding: '8px 12px'
                                        }}>
                                            Amount
                                        </TableCell>
                                        <TableCell style={{
                                            color: 'white',
                                            fontWeight: '600',
                                            fontSize: '11px',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.5px',
                                            padding: '8px 12px',
                                            textAlign: 'right'
                                        }}>
                                            Actions
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {sortedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((item, index) => (
                                        <TableRow
                                            key={item.Id}
                                            className="table-row"
                                            style={{
                                                background: index % 2 === 0 ? '#ffffff' : '#f8f9fa'
                                            }}
                                        >
                                            <TableCell style={{
                                                fontSize: '12px',
                                                padding: '6px 12px',
                                                color: '#2d3748'
                                            }}>
                                                {page * rowsPerPage + index + 1}
                                            </TableCell>
                                            <TableCell style={{
                                                fontSize: '12px',
                                                padding: '6px 12px',
                                                color: '#2d3748'
                                            }}>
                                                {formatDate(item.BillDate)}
                                            </TableCell>
                                            <TableCell style={{
                                                fontSize: '12px',
                                                padding: '6px 12px',
                                                color: '#2d3748'
                                            }}>
                                                {item.BillNo}/{item.FinYear}
                                            </TableCell>
                                            <TableCell style={{
                                                fontSize: '12px',
                                                padding: '6px 12px',
                                                color: '#2d3748'
                                            }}>
                                                {item.AccountHeadName}
                                            </TableCell>
                                            <TableCell style={{
                                                fontSize: '12px',
                                                padding: '6px 12px',
                                                color: item.IsTypeDescription === 'Debit' ? '#e53e3e' : '#38a169',
                                                fontWeight: '600'
                                            }}>
                                                {item.IstypeName}
                                            </TableCell>
                                            <TableCell style={{
                                                fontSize: '12px',
                                                padding: '6px 12px',
                                                color: '#2d3748',
                                                fontWeight: '600'
                                            }}>
                                                {item.TotalAmount ? item.TotalAmount.toFixed(2) : "0.00"}
                                            </TableCell>
                                            <TableCell style={{ padding: '6px 12px', textAlign: 'right' }}>
                                                <Tooltip title="View Details" arrow>
                                                    <IconButton size="small" component={Link} to={`/IncomeExpenseDetails/${item.Id}`}>
                                                        <Info style={{ color: 'darksalmon', fontSize: '19px' }} />
                                                    </IconButton>
                                                </Tooltip>
                                                {hasEditMasterPermission && (
                                                    <Tooltip title="Edit" arrow>
                                                        <IconButton size="small" component={Link} to={`/IncomeExpenseEdit/${item.Id}`}>
                                                            <EditIcon style={{ fontSize: '19px', color: '#667eea' }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                                {hasDeleteMasterPermission && (
                                                    <Tooltip title={item.IsActive ? "Delete" : "Activate"} arrow>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => (item.IsActive ? openConfirmDialog(item.Id) : handleActivate(item.Id))}
                                                            color="error"
                                                        >
                                                            {item.IsActive ? (
                                                                <DeleteIcon style={{ fontSize: '19px' }} />
                                                            ) : (
                                                                <ActivateIcon style={{ color: 'darkolivegreen', fontSize: '19px' }} />
                                                            )}
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                            <TablePagination
                                rowsPerPageOptions={[5, 10, 25]}
                                component="div"
                                count={filteredData.length}
                                rowsPerPage={rowsPerPage}
                                page={page}
                                onPageChange={handleChangePage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                            />
                        </TableContainer>
                    </div>
                </div>

                {/* Delete Confirmation Dialog */}
                <Dialog
                    open={confirmDialogOpen}
                    onClose={closeConfirmDialog}
                    aria-labelledby="alert-dialog-title"
                    aria-describedby="alert-dialog-description"
                    PaperProps={{
                        style: {
                            borderRadius: '12px',
                            padding: '8px'
                        }
                    }}
                >
                    <DialogTitle id="alert-dialog-title" style={{
                        fontSize: '16px',
                        fontWeight: '600',
                        color: '#2d3748'
                    }}>
                        Delete Record?
                    </DialogTitle>
                    <DialogContent>
                        <DialogContentText id="alert-dialog-description" style={{
                            fontSize: '13px',
                            color: '#666'
                        }}>
                            Are you sure you want to delete this record?
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions style={{ padding: '8px 16px' }}>
                        <Button
                            onClick={closeConfirmDialog}
                            style={{
                                background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a6f 100%)',
                                color: '#ffffff',
                                borderRadius: '8px',
                                padding: '6px 16px',
                                fontSize: '13px',
                                fontWeight: '600',
                                textTransform: 'none'
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleDelete}
                            style={{
                                background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                                color: '#ffffff',
                                borderRadius: '8px',
                                padding: '6px 16px',
                                fontSize: '13px',
                                fontWeight: '600',
                                textTransform: 'none'
                            }}
                        >
                            Confirm
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Account Head Modal */}
                <Dialog
                    open={accountHeadModalOpen}
                    onClose={closeAccountHeadModal}
                    maxWidth="md"
                    fullWidth
                    PaperProps={{
                        style: {
                            borderRadius: '16px',
                            maxHeight: '90vh'
                        }
                    }}
                >
                    <DialogTitle style={{
                        background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                        color: 'white',
                        padding: '10px 20px',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}>
                        <div>
                            <h6 style={{ margin: 0, fontSize: '15px', fontWeight: '700' }}>
                                Account Head Management
                            </h6>
                            <p style={{ margin: '2px 0 0 0', fontSize: '11px', opacity: 0.9 }}>
                                Create and manage account heads
                            </p>
                        </div>
                        <IconButton
                            onClick={closeAccountHeadModal}
                            style={{
                                color: 'white',
                                background: 'rgba(255, 255, 255, 0.2)',
                                width: '30px',
                                height: '30px'
                            }}
                        >
                            ×
                        </IconButton>
                    </DialogTitle>

                    <DialogContent style={{ padding: '16px' }}>
                        {/* Success/Error Messages */}
                        {accountHeadSuccess && (
                            <div style={{
                                background: 'linear-gradient(135deg, #d4edda 0%, #c3e6cb 100%)',
                                border: '1px solid #c3e6cb',
                                borderRadius: '6px',
                                padding: '8px 12px',
                                marginBottom: '12px',
                                color: '#155724',
                                fontSize: '12px',
                                fontWeight: '500'
                            }}>
                                ✓ {accountHeadSuccess}
                            </div>
                        )}

                        {accountHeadError && (
                            <div style={{
                                background: 'linear-gradient(135deg, #f8d7da 0%, #f5c6cb 100%)',
                                border: '1px solid #f5c6cb',
                                borderRadius: '6px',
                                padding: '8px 12px',
                                marginBottom: '12px',
                                color: '#721c24',
                                fontSize: '12px',
                                fontWeight: '500'
                            }}>
                                ⚠ {accountHeadError}
                            </div>
                        )}

                        {/* Create/Edit Form */}
                        <div style={{
                            background: '#f8f9fa',
                            borderRadius: '8px',
                            padding: '12px',
                            marginBottom: '16px'
                        }}>
                            <h6 style={{
                                fontSize: '13px',
                                fontWeight: '600',
                                marginBottom: '10px',
                                color: '#2d3748'
                            }}>
                                {editAccountHeadId ? 'Edit Account Head' : 'Create New Account Head'}
                            </h6>

                            <form onSubmit={handleAccountHeadSubmit}>
                                <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                                    <div style={{ flex: '1', minWidth: '200px' }}>
                                        <label style={{
                                            display: 'block',
                                            fontSize: '11px',
                                            fontWeight: '600',
                                            marginBottom: '4px',
                                            color: '#4a5568'
                                        }}>
                                            Account Head Name <span style={{ color: '#e53e3e' }}>*</span>
                                        </label>
                                        <input
                                            type="text"
                                            value={accountHeadName}
                                            onChange={(e) => setAccountHeadName(e.target.value)}
                                            placeholder="Enter account head name"
                                            style={{
                                                width: '100%',
                                                padding: '6px 10px',
                                                border: '2px solid #e2e8f0',
                                                borderRadius: '6px',
                                                fontSize: '12px',
                                                outline: 'none',
                                                transition: 'border-color 0.2s',
                                                height: '32px'
                                            }}
                                            onFocus={(e) => e.target.style.borderColor = '#f093fb'}
                                            onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                                        />
                                    </div>

                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '6px',
                                        padding: '6px 12px',
                                        background: 'white',
                                        borderRadius: '6px',
                                        border: '2px solid #e2e8f0',
                                        height: '32px'
                                    }}>
                                        <input
                                            type="checkbox"
                                            id="isExpenseOrIncome"
                                            checked={isExpenseOrIncome}
                                            onChange={(e) => setIsExpenseOrIncome(e.target.checked)}
                                            style={{
                                                width: '16px',
                                                height: '16px',
                                                cursor: 'pointer',
                                                accentColor: '#f093fb'
                                            }}
                                        />
                                        <label
                                            htmlFor="isExpenseOrIncome"
                                            style={{
                                                fontSize: '12px',
                                                fontWeight: '600',
                                                color: '#4a5568',
                                                cursor: 'pointer',
                                                userSelect: 'none'
                                            }}
                                        >
                                            Is Expense/Income
                                        </label>
                                    </div>

                                    <Button
                                        type="submit"
                                        variant="contained"
                                        disabled={accountHeadLoading}
                                        startIcon={accountHeadLoading ? <CircularProgress size={14} color="inherit" /> : <AddIcon style={{ fontSize: '16px' }} />}
                                        style={{
                                            background: editAccountHeadId
                                                ? 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'
                                                : 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                                            color: 'white',
                                            padding: '6px 16px',
                                            borderRadius: '6px',
                                            fontSize: '12px',
                                            fontWeight: '600',
                                            textTransform: 'none',
                                            boxShadow: '0 2px 6px rgba(240, 147, 251, 0.3)',
                                            height: '32px'
                                        }}
                                    >
                                        {editAccountHeadId ? 'Update' : 'Add'}
                                    </Button>

                                    {editAccountHeadId && (
                                        <Button
                                            onClick={() => {
                                                setEditAccountHeadId(null);
                                                setAccountHeadName('');
                                                setIsExpenseOrIncome(false);
                                                setAccountHeadError('');
                                            }}
                                            variant="outlined"
                                            style={{
                                                padding: '6px 16px',
                                                borderRadius: '6px',
                                                fontSize: '12px',
                                                fontWeight: '600',
                                                textTransform: 'none',
                                                borderColor: '#cbd5e0',
                                                color: '#4a5568',
                                                height: '32px'
                                            }}
                                        >
                                            Cancel
                                        </Button>
                                    )}
                                </div>
                            </form>
                        </div>

                        {/* Search */}
                        <div style={{ marginBottom: '12px' }}>
                            <input
                                type="text"
                                placeholder="Search account heads..."
                                value={accountHeadSearchQuery}
                                onChange={(e) => setAccountHeadSearchQuery(e.target.value)}
                                style={{
                                    width: '100%',
                                    padding: '6px 12px',
                                    border: '2px solid #e2e8f0',
                                    borderRadius: '6px',
                                    fontSize: '12px',
                                    outline: 'none',
                                    height: '32px'
                                }}
                                onFocus={(e) => e.target.style.borderColor = '#f093fb'}
                                onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                            />
                        </div>

                        {/* Account Head List */}
                        <div style={{
                            maxHeight: '400px',
                            overflowY: 'auto',
                            border: '1px solid #e2e8f0',
                            borderRadius: '12px'
                        }}>
                            {accountHeadLoading ? (
                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    padding: '40px'
                                }}>
                                    <CircularProgress size={40} style={{ color: '#f093fb' }} />
                                </div>
                            ) : filteredAccountHeadData.length === 0 ? (
                                <div style={{
                                    textAlign: 'center',
                                    padding: '40px',
                                    color: '#a0aec0',
                                    fontSize: '14px'
                                }}>
                                    No account heads found
                                </div>
                            ) : (
                                <Table>
                                    <TableHead>
                                        <TableRow style={{ background: '#f7fafc' }}>
                                            <TableCell style={{
                                                fontWeight: '600',
                                                fontSize: '10px',
                                                textTransform: 'uppercase',
                                                color: '#4a5568',
                                                padding: '8px 10px'
                                            }}>
                                                #
                                            </TableCell>
                                            <TableCell style={{
                                                fontWeight: '600',
                                                fontSize: '10px',
                                                textTransform: 'uppercase',
                                                color: '#4a5568',
                                                padding: '8px 10px'
                                            }}>
                                                Name
                                            </TableCell>
                                            <TableCell style={{
                                                fontWeight: '600',
                                                fontSize: '10px',
                                                textTransform: 'uppercase',
                                                color: '#4a5568',
                                                padding: '8px 10px'
                                            }}>
                                                Type
                                            </TableCell>
                                            <TableCell style={{
                                                fontWeight: '600',
                                                fontSize: '10px',
                                                textTransform: 'uppercase',
                                                color: '#4a5568',
                                                padding: '8px 10px',
                                                textAlign: 'right'
                                            }}>
                                                Actions
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {filteredAccountHeadData.map((item, index) => (
                                            <TableRow
                                                key={item.Id}
                                                style={{
                                                    background: index % 2 === 0 ? 'white' : '#f7fafc',
                                                    transition: 'background 0.2s'
                                                }}
                                                onMouseEnter={(e) => e.currentTarget.style.background = '#edf2f7'}
                                                onMouseLeave={(e) => e.currentTarget.style.background = index % 2 === 0 ? 'white' : '#f7fafc'}
                                            >
                                                <TableCell style={{ padding: '8px 10px', fontSize: '12px', color: '#2d3748' }}>
                                                    {index + 1}
                                                </TableCell>
                                                <TableCell style={{ padding: '8px 10px', fontSize: '12px', color: '#2d3748', fontWeight: '500' }}>
                                                    {item.Name}
                                                </TableCell>
                                                <TableCell style={{ padding: '8px 10px', fontSize: '12px' }}>
                                                    <span style={{
                                                        padding: '3px 10px',
                                                        borderRadius: '10px',
                                                        fontSize: '10px',
                                                        fontWeight: '600',
                                                        background: item.IsExpenseOrIncome
                                                            ? 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)'
                                                            : 'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)',
                                                        color: item.IsExpenseOrIncome ? '#991b1b' : '#065f46'
                                                    }}>
                                                        {item.IsExpenseOrIncome ? 'Expense' : 'Income'}
                                                    </span>
                                                </TableCell>
                                                <TableCell style={{ padding: '8px 10px', textAlign: 'right' }}>
                                                    <Tooltip title="Edit" arrow>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleEditAccountHead(item)}
                                                            style={{ color: '#f59e0b', padding: '4px' }}
                                                        >
                                                            <EditIcon style={{ fontSize: '16px' }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Delete" arrow>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleDeleteAccountHead(item.Id)}
                                                            style={{ color: '#e53e3e', padding: '4px' }}
                                                        >
                                                            <DeleteIcon style={{ fontSize: '16px' }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            )}
                        </div>
                    </DialogContent>

                    <DialogActions style={{
                        padding: '10px 16px',
                        background: '#f7fafc',
                        borderTop: '1px solid #e2e8f0'
                    }}>
                        <Button
                            onClick={closeAccountHeadModal}
                            style={{
                                padding: '6px 16px',
                                borderRadius: '6px',
                                fontSize: '12px',
                                fontWeight: '600',
                                textTransform: 'none',
                                background: 'linear-gradient(135deg, #cbd5e0 0%, #a0aec0 100%)',
                                color: 'white',
                                height: '32px'
                            }}
                        >
                            Close
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Footer */}
                <div style={{
                    position: 'fixed',
                    bottom: '20px',
                    right: '20px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '12px 20px',
                    background: 'rgba(255, 255, 255, 0.95)',
                    borderRadius: '16px',
                    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
                    fontFamily: 'serif',
                    fontWeight: '600',
                    color: '#2c3e50',
                    fontSize: '13px',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255, 255, 255, 0.3)',
                    zIndex: 1000
                }}>
                    <CopyrightIcon style={{ fontSize: '18px', color: '#667eea' }} />
                    <span>EFFTABS SOLUTIONS LLP</span>
                </div>
            </div>
        </Fade>
    );
}

function descendingComparator(a, b, orderBy) {
    if (b[orderBy] < a[orderBy]) {
        return -1;
    }
    if (b[orderBy] > a[orderBy]) {
        return 1;
    }
    return 0;
}

function getComparator(order, orderBy) {
    return order === 'desc'
        ? (a, b) => descendingComparator(a, b, orderBy)
        : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
    const stabilizedThis = array.map((el, index) => [el, index]);
    stabilizedThis.sort((a, b) => {
        const order = comparator(a[0], b[0]);
        if (order !== 0) return order;
        return a[1] - b[1];
    });
    return stabilizedThis.map((el) => el[0]);
}

export default IncomeExpenseIndex;

